import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { api, workspaceAdminAPI } from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import {
  WHITE_LABEL_ADDON,
  attachCustomDomain,
  isPortalHost,
  normaliseHost,
} from '../lib/tenantCustomDomain';
import PlatformGovernancePanel from '../components/superadmin/PlatformGovernancePanel';
import { useAuth } from '../contexts/AuthContext';
import { Network, GitBranch, ShieldCheck, Receipt, Landmark } from 'lucide-react';
import { EmptyState, CardSkeleton } from '../components/common/States';

const flattenTree = (nodes = [], list = []) => {
  nodes.forEach((node) => {
    list.push(node);
    flattenTree(node.children || [], list);
  });
  return list;
};

const parseCsvValues = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const TreeNode = ({ node, level = 0 }) => (
  <div className="space-y-2">
    <div className="border rounded-md p-3 bg-background" style={{ marginLeft: `${level * 16}px` }}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium">{node.name}</p>
        <Badge variant="outline">{node.slug}</Badge>
        <Badge variant="secondary">{node.tenant_type}</Badge>
        <Badge variant={node.isolation_level === 'isolated' ? 'warning' : 'success'}>
          {node.isolation_level}
        </Badge>
        <Badge variant={node.can_create_sub_tenant ? 'default' : 'outline'}>
          {node.can_create_sub_tenant ? 'child-create on' : 'child-create off'}
        </Badge>
        <Badge variant="outline">L{node.tenant_level ?? node.hierarchy_depth ?? 0}</Badge>
        {node.subscription?.plan_code ? <Badge variant="info">{node.subscription.plan_code}</Badge> : null}
      </div>
      {Array.isArray(node.permission_groups) && node.permission_groups.length > 0 ? (
        <p className="text-xs text-muted-foreground mt-2">
          Permission groups: {node.permission_groups.map((group) => group.display_name || group.group_key).join(', ')}
        </p>
      ) : null}
    </div>
    {(node.children || []).map((child) => (
      <TreeNode key={child.id} node={child} level={level + 1} />
    ))}
  </div>
);

const CONTROL_PLANE_SECTIONS = [
  {
    key: 'hierarchy',
    id: 'control/hierarchy',
    label: 'Hierarchy',
    description: 'Browse the tenant tree and cloud topology',
    icon: Network,
  },
  {
    key: 'create-tenant',
    id: 'control/create-tenant',
    label: 'Create Tenant',
    description: 'Create tenant nodes and define hierarchy rules',
    icon: GitBranch,
  },
  {
    key: 'access',
    id: 'control/access',
    label: 'Access & Roles',
    description: 'Manage memberships, permission groups, and effective roles',
    icon: ShieldCheck,
  },
  {
    key: 'billing',
    id: 'control/billing',
    label: 'Billing',
    description: 'Manage billing accounts and generate invoices',
    icon: Receipt,
  },
  {
    key: 'governance',
    id: 'control/governance',
    label: 'Governance',
    description: 'Review platform governance controls and policies',
    icon: Landmark,
  },
];

const SuperadminControlPlanePage = ({ activeTab = 'control/hierarchy', onNavigate }) => {
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';

  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState([]);
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [effectiveRoles, setEffectiveRoles] = useState({ roles: [], role_hierarchy: [] });

  const [tenantForm, setTenantForm] = useState({
    slug: '',
    name: '',
    parent_tenant_id: '',
    tenant_type: 'enterprise',
    isolation_level: 'shared',
    billing_account_id: '',
    billing_model: 'parent_paid',
    can_create_sub_tenant: true,
    allowed_modules: '',
    permission_group_keys: '',
    role_templates: '',
    employee_limit: '',
    storage_limit_mb: '',
    custom_domain: '',
  });

  // Only to label the custom-domain field with the suffix a workspace already
  // answers on. A failure costs a label, not the page.
  const [portalDomain, setPortalDomain] = useState('');
  useEffect(() => {
    let mounted = true;
    api.get('/api/branding')
      .then(({ data }) => mounted && setPortalDomain(String(data?.portal_domain || '')))
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const [membershipForm, setMembershipForm] = useState({
    user_id: '',
    tenant_id: '',
    role: 'workspace_admin',
    membership_type: 'owner',
    is_primary: false,
    can_create_sub_tenant: false,
    manage_descendants: false,
  });

  const [permissionGroupForm, setPermissionGroupForm] = useState({
    group_key: '',
    display_name: '',
    description: '',
    tenant_id: '',
    permissions: '',
  });

  const [permissionAssignmentForm, setPermissionAssignmentForm] = useState({
    tenant_id: '',
    permission_group_keys: '',
  });

  const [billingForm, setBillingForm] = useState({
    account_name: '',
    legal_entity_name: '',
    email: '',
    invoice_mode: 'consolidated',
  });

  const [invoiceForm, setInvoiceForm] = useState({
    billing_account_id: '',
    tenant_id: '',
    invoice_scope: 'consolidated',
    period_start: '',
    period_end: '',
    currency_code: 'INR',
  });

  const tenantsFlat = useMemo(() => flattenTree(tree, []), [tree]);
  const currentSectionKey = useMemo(() => {
    const rawSection = String(activeTab || '')
      .replace(/^control\/?/, '')
      .trim()
      .toLowerCase();
    if (!rawSection || rawSection === 'control') {
      return 'hierarchy';
    }
    return CONTROL_PLANE_SECTIONS.some((section) => section.key === rawSection) ? rawSection : 'hierarchy';
  }, [activeTab]);

  const currentSection = useMemo(
    () => CONTROL_PLANE_SECTIONS.find((section) => section.key === currentSectionKey) || CONTROL_PLANE_SECTIONS[0],
    [currentSectionKey]
  );

  const navigationCards = useMemo(
    () => [
      {
        ...CONTROL_PLANE_SECTIONS.find((section) => section.key === 'hierarchy'),
        metric: `${tenantsFlat.length} tenant nodes`,
      },
      {
        ...CONTROL_PLANE_SECTIONS.find((section) => section.key === 'create-tenant'),
        metric: `${tenantsFlat.filter((row) => row.can_create_sub_tenant).length} child-create enabled`,
      },
      {
        ...CONTROL_PLANE_SECTIONS.find((section) => section.key === 'access'),
        metric: `${memberships.length} memberships`,
      },
      {
        ...CONTROL_PLANE_SECTIONS.find((section) => section.key === 'billing'),
        metric: `${billingAccounts.length} billing accounts`,
      },
      {
        ...CONTROL_PLANE_SECTIONS.find((section) => section.key === 'governance'),
        metric: `${permissionGroups.length} permission groups`,
      },
    ],
    [billingAccounts.length, memberships.length, permissionGroups.length, tenantsFlat]
  );

  const loadEffectiveRoles = useCallback(async (tenantId) => {
    if (!tenantId) {
      setEffectiveRoles({ roles: [], role_hierarchy: [] });
      return;
    }
    try {
      const response = await workspaceAdminAPI.getEffectiveRoles({ tenant_id: Number(tenantId) });
      setEffectiveRoles(response?.data || { roles: [], role_hierarchy: [] });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load effective roles'));
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const requests = [
        workspaceAdminAPI.getTenantHierarchy(),
        workspaceAdminAPI.listTenantMemberships(),
        workspaceAdminAPI.listPermissionGroups(),
      ];
      if (isSuperadmin) {
        requests.push(workspaceAdminAPI.listBillingAccounts(), workspaceAdminAPI.listInvoices());
      }
      const responses = await Promise.all(requests);
      const [treeRes, membershipRes, permissionGroupRes, billingRes, invoiceRes] = responses;
      const nextTree = Array.isArray(treeRes?.data) ? treeRes.data : [];
      setTree(nextTree);
      setMemberships(Array.isArray(membershipRes?.data) ? membershipRes.data : []);
      setPermissionGroups(Array.isArray(permissionGroupRes?.data) ? permissionGroupRes.data : []);
      setBillingAccounts(Array.isArray(billingRes?.data) ? billingRes.data : []);
      setInvoices(Array.isArray(invoiceRes?.data) ? invoiceRes.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load control plane data'));
    } finally {
      setLoading(false);
    }
  }, [isSuperadmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!permissionAssignmentForm.tenant_id && tenantsFlat[0]?.id) {
      setPermissionAssignmentForm((prev) => ({ ...prev, tenant_id: String(tenantsFlat[0].id) }));
      setMembershipForm((prev) => ({ ...prev, tenant_id: prev.tenant_id || String(tenantsFlat[0].id) }));
    }
  }, [tenantsFlat, permissionAssignmentForm.tenant_id]);

  useEffect(() => {
    if (permissionAssignmentForm.tenant_id) {
      loadEffectiveRoles(permissionAssignmentForm.tenant_id);
    }
  }, [loadEffectiveRoles, permissionAssignmentForm.tenant_id]);

  const createTenantNode = async () => {
    const customDomain = normaliseHost(tenantForm.custom_domain);
    // Checked before the tenant exists, because the tenant is created first and
    // cannot be un-created. `assert_custom_domain_allowed` refuses this too.
    if (isPortalHost(customDomain, portalDomain)) {
      toast.error(
        `${customDomain} is part of the platform's own domain. This workspace already answers on `
        + `${tenantForm.slug ? `${tenantForm.slug}.${portalDomain}` : `<slug>.${portalDomain}`}`
        + ' — this field is for a domain the tenant owns, such as hr.theircompany.com.'
      );
      return;
    }
    // Stripped rather than left to Pydantic's default of ignoring unknown
    // fields: the domain lives on tenant_branding, and this endpoint has no
    // such field to accept.
    const { custom_domain: _customDomainField, ...tenantPayload } = tenantForm;
    try {
      const created = await workspaceAdminAPI.createTenantNode({
        ...tenantPayload,
        parent_tenant_id: tenantForm.parent_tenant_id ? Number(tenantForm.parent_tenant_id) : null,
        billing_account_id: tenantForm.billing_account_id ? Number(tenantForm.billing_account_id) : null,
        can_create_sub_tenant: Boolean(tenantForm.can_create_sub_tenant),
        allowed_modules: parseCsvValues(tenantForm.allowed_modules),
        permission_group_keys: parseCsvValues(tenantForm.permission_group_keys),
        role_templates: parseCsvValues(tenantForm.role_templates),
        employee_limit: tenantForm.employee_limit ? Number(tenantForm.employee_limit) : null,
        storage_limit_mb: tenantForm.storage_limit_mb ? Number(tenantForm.storage_limit_mb) : null,
      });

      // The tenant is committed by now, so the domain is attached separately and
      // a failure here is reported as its own — rolling the workspace back over
      // it would discard the memberships and the subscription with it.
      const createdTenantId = Number(created?.data?.id);
      if (createdTenantId && customDomain) {
        const outcome = await attachCustomDomain(workspaceAdminAPI, createdTenantId, customDomain);
        if (!outcome.ok) {
          toast.warning(
            `Tenant node created, but ${outcome.failedStep} failed: `
            + `${getErrorMessage(outcome.error, 'no reason given')}. Set it from the workspace's Rebranding screen.`
          );
        } else if (!(created?.data?.limits?.addons || []).includes(WHITE_LABEL_ADDON)) {
          // Stored but inert. This form assigns no plan, so the add-on that
          // makes a custom domain resolve at all has to be granted separately —
          // saying "saved" without saying that would be a lie of omission.
          toast.warning(
            `Tenant node created and ${customDomain} saved, but this tenant has no white-label `
            + 'add-on, so the domain will not be served. Grant it on the subscription first.'
          );
        } else {
          toast.success(`Tenant node created. ${customDomain} saved — not live until its DNS records are published.`);
        }
      } else {
        toast.success('Tenant node created');
      }
      setTenantForm((prev) => ({
        ...prev,
        slug: '',
        name: '',
        custom_domain: '',
        allowed_modules: '',
        permission_group_keys: '',
        role_templates: '',
        employee_limit: '',
        storage_limit_mb: '',
      }));
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create tenant node'));
    }
  };

  const createMembership = async () => {
    try {
      await workspaceAdminAPI.upsertTenantMembership({
        user_id: Number(membershipForm.user_id),
        tenant_id: Number(membershipForm.tenant_id),
        role: membershipForm.role,
        membership_type: membershipForm.membership_type,
        is_primary: membershipForm.is_primary,
        is_active: true,
        scope_policy: {
          can_create_sub_tenant: membershipForm.can_create_sub_tenant,
          manage_descendants: membershipForm.manage_descendants,
        },
      });
      toast.success('Membership updated');
      setMembershipForm((prev) => ({ ...prev, user_id: '' }));
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update membership'));
    }
  };

  const removeMembership = async (membershipId) => {
    try {
      await workspaceAdminAPI.deleteTenantMembership(membershipId);
      toast.success('Membership removed');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to remove membership'));
    }
  };

  const createPermissionGroup = async () => {
    try {
      await workspaceAdminAPI.upsertPermissionGroup({
        group_key: permissionGroupForm.group_key,
        display_name: permissionGroupForm.display_name,
        description: permissionGroupForm.description,
        tenant_id: permissionGroupForm.tenant_id ? Number(permissionGroupForm.tenant_id) : null,
        permissions: parseCsvValues(permissionGroupForm.permissions),
      });
      toast.success('Permission group saved');
      setPermissionGroupForm({ group_key: '', display_name: '', description: '', tenant_id: '', permissions: '' });
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save permission group'));
    }
  };

  const assignPermissionGroups = async () => {
    try {
      await workspaceAdminAPI.assignTenantPermissionGroups(Number(permissionAssignmentForm.tenant_id), {
        permission_group_keys: parseCsvValues(permissionAssignmentForm.permission_group_keys),
      });
      toast.success('Permission groups assigned');
      await loadData();
      await loadEffectiveRoles(permissionAssignmentForm.tenant_id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to assign permission groups'));
    }
  };

  const createBillingAccount = async () => {
    try {
      await workspaceAdminAPI.createBillingAccount(billingForm);
      toast.success('Billing account created');
      setBillingForm({ account_name: '', legal_entity_name: '', email: '', invoice_mode: 'consolidated' });
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create billing account'));
    }
  };

  const generateInvoice = async () => {
    try {
      await workspaceAdminAPI.createInvoice({
        ...invoiceForm,
        billing_account_id: Number(invoiceForm.billing_account_id),
        tenant_id: invoiceForm.tenant_id ? Number(invoiceForm.tenant_id) : null,
      });
      toast.success('Invoice generated');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate invoice'));
    }
  };

  const collectUsage = async () => {
    try {
      await workspaceAdminAPI.collectUsageMetrics();
      toast.success('Usage metrics collected');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to collect usage'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <CardSkeleton key={i} rows={2} />
          ))}
        </div>
        <CardSkeleton rows={6} />
      </div>
    );
  }

  const renderHierarchySection = () => (
    <Card>
      <CardHeader><CardTitle>Tenant Hierarchy</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {tree.length === 0 ? (
          <EmptyState
            icon={Network}
            title="No tenant nodes found"
            description="Create a tenant node to start building your cloud topology."
          />
        ) : (
          tree.map((node) => <TreeNode key={node.id} node={node} />)
        )}
      </CardContent>
    </Card>
  );

  const renderCreateTenantSection = () => (
    <Card>
      <CardHeader><CardTitle>Create Tenant</CardTitle></CardHeader>
      <CardContent className="grid md:grid-cols-4 gap-3">
        <div>
          <Label>Name</Label>
          <Input value={tenantForm.name} onChange={(e) => setTenantForm((p) => ({ ...p, name: e.target.value }))} />
        </div>
        <div>
          <Label>Slug</Label>
          <Input value={tenantForm.slug} onChange={(e) => setTenantForm((p) => ({ ...p, slug: e.target.value }))} />
        </div>
        <div>
          <Label>Parent Tenant</Label>
          <Select
            value={tenantForm.parent_tenant_id || '_none'}
            onValueChange={(v) => setTenantForm((p) => ({ ...p, parent_tenant_id: v === '_none' ? '' : v }))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">Root Tenant</SelectItem>
              {tenantsFlat.map((row) => (
                <SelectItem key={row.id} value={String(row.id)}>{row.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Tenant Type</Label>
          <Select value={tenantForm.tenant_type} onValueChange={(v) => setTenantForm((p) => ({ ...p, tenant_type: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="enterprise">enterprise</SelectItem>
              <SelectItem value="department">department</SelectItem>
              <SelectItem value="vendor">vendor</SelectItem>
              <SelectItem value="franchise">franchise</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Isolation</Label>
          <Select value={tenantForm.isolation_level} onValueChange={(v) => setTenantForm((p) => ({ ...p, isolation_level: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="shared">shared</SelectItem>
              <SelectItem value="isolated">isolated</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Allowed Modules</Label>
          <Input
            placeholder="hr, attendance, payroll"
            value={tenantForm.allowed_modules}
            onChange={(e) => setTenantForm((p) => ({ ...p, allowed_modules: e.target.value }))}
          />
        </div>
        <div>
          <Label>Permission Groups</Label>
          <Input
            placeholder="hr, attendance"
            value={tenantForm.permission_group_keys}
            onChange={(e) => setTenantForm((p) => ({ ...p, permission_group_keys: e.target.value }))}
          />
        </div>
        <div>
          <Label>Role Templates</Label>
          <Input
            placeholder="workspace_admin, hr_admin"
            value={tenantForm.role_templates}
            onChange={(e) => setTenantForm((p) => ({ ...p, role_templates: e.target.value }))}
          />
        </div>
        <div>
          <Label>Employee Limit</Label>
          <Input
            type="number"
            value={tenantForm.employee_limit}
            onChange={(e) => setTenantForm((p) => ({ ...p, employee_limit: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <Label>Custom Domain <span className="font-normal text-muted-foreground">(optional)</span></Label>
          <Input
            placeholder="hr.theircompany.com"
            value={tenantForm.custom_domain}
            onChange={(e) => setTenantForm((p) => ({ ...p, custom_domain: e.target.value }))}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            A domain the tenant owns. Saved against their branding and served once they publish the
            DNS records and it is verified. This workspace already answers on{' '}
            <span className="font-medium text-foreground">
              {tenantForm.slug ? `${tenantForm.slug}.${portalDomain || 'the portal domain'}` : `<slug>.${portalDomain || 'the portal domain'}`}
            </span>{' '}
            with nothing to set up — do not enter that here.
          </p>
        </div>
        <div>
          <Label>Storage Limit MB</Label>
          <Input
            type="number"
            value={tenantForm.storage_limit_mb}
            onChange={(e) => setTenantForm((p) => ({ ...p, storage_limit_mb: e.target.value }))}
          />
        </div>
        {isSuperadmin ? (
          <>
            <div>
              <Label>Billing Account</Label>
              <Select
                value={tenantForm.billing_account_id || '_none'}
                onValueChange={(v) => setTenantForm((p) => ({ ...p, billing_account_id: v === '_none' ? '' : v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {billingAccounts.map((row) => (
                    <SelectItem key={row.id} value={String(row.id)}>{row.account_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Billing Model</Label>
              <Select value={tenantForm.billing_model} onValueChange={(v) => setTenantForm((p) => ({ ...p, billing_model: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="parent_paid">parent_paid</SelectItem>
                  <SelectItem value="subtenant_paid">tenant_paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        ) : null}
        <div className="md:col-span-2 flex items-center gap-2 pt-6">
          <input
            id="tenant-can-create-sub"
            type="checkbox"
            checked={tenantForm.can_create_sub_tenant}
            onChange={(e) => setTenantForm((p) => ({ ...p, can_create_sub_tenant: e.target.checked }))}
          />
          <Label htmlFor="tenant-can-create-sub">Allow this tenant to create child tenants</Label>
        </div>
        <div className="md:col-span-2 flex items-end justify-end">
          <Button onClick={createTenantNode}>Create Tenant</Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderAccessSection = () => (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Membership Management</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <Input
                placeholder="User ID"
                type="number"
                value={membershipForm.user_id}
                onChange={(e) => setMembershipForm((p) => ({ ...p, user_id: e.target.value }))}
              />
              <Select value={membershipForm.tenant_id || '_none'} onValueChange={(v) => setMembershipForm((p) => ({ ...p, tenant_id: v === '_none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Tenant" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Select tenant</SelectItem>
                  {tenantsFlat.map((row) => (
                    <SelectItem key={row.id} value={String(row.id)}>{row.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={membershipForm.role} onValueChange={(v) => setMembershipForm((p) => ({ ...p, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="workspace_admin">workspace_admin</SelectItem>
                  <SelectItem value="hr_admin">hr_admin</SelectItem>
                  <SelectItem value="manager">manager</SelectItem>
                  <SelectItem value="hr_manager">hr_manager</SelectItem>
                  <SelectItem value="employee">employee</SelectItem>
                </SelectContent>
              </Select>
              <Select value={membershipForm.membership_type} onValueChange={(v) => setMembershipForm((p) => ({ ...p, membership_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">owner</SelectItem>
                  <SelectItem value="admin">admin</SelectItem>
                  <SelectItem value="manager">manager</SelectItem>
                  <SelectItem value="member">member</SelectItem>
                  <SelectItem value="viewer">viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={membershipForm.is_primary}
                  onChange={(e) => setMembershipForm((p) => ({ ...p, is_primary: e.target.checked }))}
                />
                Primary
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={membershipForm.can_create_sub_tenant}
                  onChange={(e) => setMembershipForm((p) => ({ ...p, can_create_sub_tenant: e.target.checked }))}
                />
                Can create child tenants
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={membershipForm.manage_descendants}
                  onChange={(e) => setMembershipForm((p) => ({ ...p, manage_descendants: e.target.checked }))}
                />
                Manage descendants
              </label>
            </div>
            <Button onClick={createMembership}>Save Membership</Button>
            <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
              {memberships.length === 0 ? (
                <EmptyState
                  compact
                  icon={ShieldCheck}
                  title="No memberships yet"
                  description="Assign a user to a tenant to grant access."
                />
              ) : (
                memberships.slice(0, 12).map((row) => (
                  <div key={row.id} className="border rounded-md p-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium">User {row.user_id} on tenant {row.tenant_id}</p>
                      <p className="text-xs text-muted-foreground">
                        {row.role} • {row.membership_type} • {row.is_primary ? 'primary' : 'secondary'}
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => removeMembership(row.id)}>Remove</Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Permission Groups</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <Input
                placeholder="Group key"
                value={permissionGroupForm.group_key}
                onChange={(e) => setPermissionGroupForm((p) => ({ ...p, group_key: e.target.value }))}
              />
              <Input
                placeholder="Display name"
                value={permissionGroupForm.display_name}
                onChange={(e) => setPermissionGroupForm((p) => ({ ...p, display_name: e.target.value }))}
              />
              <Input
                placeholder="Description"
                value={permissionGroupForm.description}
                onChange={(e) => setPermissionGroupForm((p) => ({ ...p, description: e.target.value }))}
              />
              <Select
                value={permissionGroupForm.tenant_id || '_global'}
                onValueChange={(v) => setPermissionGroupForm((p) => ({ ...p, tenant_id: v === '_global' ? '' : v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_global">Global</SelectItem>
                  {tenantsFlat.map((row) => (
                    <SelectItem key={row.id} value={String(row.id)}>{row.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="md:col-span-2">
                <Input
                  placeholder="employees.read, leave.write"
                  value={permissionGroupForm.permissions}
                  onChange={(e) => setPermissionGroupForm((p) => ({ ...p, permissions: e.target.value }))}
                />
              </div>
            </div>
            <Button onClick={createPermissionGroup}>Save Permission Group</Button>
            <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
              {permissionGroups.length === 0 ? (
                <EmptyState
                  compact
                  icon={Landmark}
                  title="No permission groups"
                  description="Create a permission group to bundle access rights."
                />
              ) : (
                permissionGroups.map((row) => (
                  <div key={row.id} className="border rounded-md p-3">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{row.display_name}</p>
                      <Badge variant="outline">{row.group_key}</Badge>
                      <Badge variant={row.is_system ? 'secondary' : 'outline'}>
                        {row.is_system ? 'system' : `tenant:${row.tenant_id ?? 'global'}`}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{(row.permissions || []).join(', ')}</p>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Assign Permission Groups</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Select
              value={permissionAssignmentForm.tenant_id || '_none'}
              onValueChange={(v) => setPermissionAssignmentForm((p) => ({ ...p, tenant_id: v === '_none' ? '' : v }))}
            >
              <SelectTrigger><SelectValue placeholder="Tenant" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Select tenant</SelectItem>
                {tenantsFlat.map((row) => (
                  <SelectItem key={row.id} value={String(row.id)}>{row.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="hr, attendance, reports"
              value={permissionAssignmentForm.permission_group_keys}
              onChange={(e) => setPermissionAssignmentForm((p) => ({ ...p, permission_group_keys: e.target.value }))}
            />
            <Button onClick={assignPermissionGroups}>Assign Groups</Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Effective Roles</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
              {(effectiveRoles.roles || []).map((row) => (
                <div key={`${row.tenant_id}-${row.role_key}-${row.source}`} className="border rounded-md p-3">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{row.role_key}</p>
                    <Badge variant="outline">{row.source}</Badge>
                    <Badge variant="secondary">tenant {row.tenant_id}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{(row.permissions || []).join(', ') || 'No permissions'}</p>
                </div>
              ))}
              {(!effectiveRoles.roles || effectiveRoles.roles.length === 0) ? (
                <EmptyState
                  compact
                  icon={ShieldCheck}
                  title="No effective role bindings"
                  description="No effective role bindings for the selected tenant."
                />
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderBillingSection = () => (
    isSuperadmin ? (
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Membership Management</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <Input
                placeholder="User ID"
                type="number"
                value={membershipForm.user_id}
                onChange={(e) => setMembershipForm((p) => ({ ...p, user_id: e.target.value }))}
              />
              <Select value={membershipForm.tenant_id || '_none'} onValueChange={(v) => setMembershipForm((p) => ({ ...p, tenant_id: v === '_none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Tenant" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Select tenant</SelectItem>
                  {tenantsFlat.map((row) => (
                    <SelectItem key={row.id} value={String(row.id)}>{row.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={membershipForm.role} onValueChange={(v) => setMembershipForm((p) => ({ ...p, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="workspace_admin">workspace_admin</SelectItem>
                  <SelectItem value="hr_admin">hr_admin</SelectItem>
                  <SelectItem value="manager">manager</SelectItem>
                  <SelectItem value="hr_manager">hr_manager</SelectItem>
                  <SelectItem value="employee">employee</SelectItem>
                </SelectContent>
              </Select>
              <Select value={membershipForm.membership_type} onValueChange={(v) => setMembershipForm((p) => ({ ...p, membership_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">owner</SelectItem>
                  <SelectItem value="admin">admin</SelectItem>
                  <SelectItem value="manager">manager</SelectItem>
                  <SelectItem value="member">member</SelectItem>
                  <SelectItem value="viewer">viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={membershipForm.is_primary}
                  onChange={(e) => setMembershipForm((p) => ({ ...p, is_primary: e.target.checked }))}
                />
                Primary
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={membershipForm.can_create_sub_tenant}
                  onChange={(e) => setMembershipForm((p) => ({ ...p, can_create_sub_tenant: e.target.checked }))}
                />
                Can create child tenants
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={membershipForm.manage_descendants}
                  onChange={(e) => setMembershipForm((p) => ({ ...p, manage_descendants: e.target.checked }))}
                />
                Manage descendants
              </label>
            </div>
            <Button onClick={createMembership}>Save Membership</Button>
            <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
              {memberships.length === 0 ? (
                <EmptyState
                  compact
                  icon={ShieldCheck}
                  title="No memberships yet"
                  description="Assign a user to a tenant to grant access."
                />
              ) : (
                memberships.slice(0, 12).map((row) => (
                  <div key={row.id} className="border rounded-md p-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium">User {row.user_id} on tenant {row.tenant_id}</p>
                      <p className="text-xs text-muted-foreground">
                        {row.role} • {row.membership_type} • {row.is_primary ? 'primary' : 'secondary'}
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => removeMembership(row.id)}>Remove</Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Permission Groups</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <Input
                placeholder="Group key"
                value={permissionGroupForm.group_key}
                onChange={(e) => setPermissionGroupForm((p) => ({ ...p, group_key: e.target.value }))}
              />
              <Input
                placeholder="Display name"
                value={permissionGroupForm.display_name}
                onChange={(e) => setPermissionGroupForm((p) => ({ ...p, display_name: e.target.value }))}
              />
              <Input
                placeholder="Description"
                value={permissionGroupForm.description}
                onChange={(e) => setPermissionGroupForm((p) => ({ ...p, description: e.target.value }))}
              />
              <Select
                value={permissionGroupForm.tenant_id || '_global'}
                onValueChange={(v) => setPermissionGroupForm((p) => ({ ...p, tenant_id: v === '_global' ? '' : v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_global">Global</SelectItem>
                  {tenantsFlat.map((row) => (
                    <SelectItem key={row.id} value={String(row.id)}>{row.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="md:col-span-2">
                <Input
                  placeholder="employees.read, leave.write"
                  value={permissionGroupForm.permissions}
                  onChange={(e) => setPermissionGroupForm((p) => ({ ...p, permissions: e.target.value }))}
                />
              </div>
            </div>
            <Button onClick={createPermissionGroup}>Save Permission Group</Button>
            <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
              {permissionGroups.length === 0 ? (
                <EmptyState
                  compact
                  icon={Landmark}
                  title="No permission groups"
                  description="Create a permission group to bundle access rights."
                />
              ) : (
                permissionGroups.map((row) => (
                  <div key={row.id} className="border rounded-md p-3">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{row.display_name}</p>
                      <Badge variant="outline">{row.group_key}</Badge>
                      <Badge variant={row.is_system ? 'secondary' : 'outline'}>
                        {row.is_system ? 'system' : `tenant:${row.tenant_id ?? 'global'}`}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{(row.permissions || []).join(', ')}</p>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    ) : (
      <Card>
        <CardHeader><CardTitle>Billing</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Billing controls are available only in superadmin view.</p>
        </CardContent>
      </Card>
    )
  );

  const renderGovernanceSection = () => <PlatformGovernancePanel />;

  const renderActiveSection = () => {
    switch (currentSectionKey) {
      case 'create-tenant':
        return renderCreateTenantSection();
      case 'access':
        return renderAccessSection();
      case 'billing':
        return renderBillingSection();
      case 'governance':
        return renderGovernanceSection();
      case 'hierarchy':
      default:
        return renderHierarchySection();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Tenant Cloud Control Plane</h2>
          <p className="text-sm text-muted-foreground">
            Hierarchical tenant governance, delegated child-tenant controls, and access inheritance management.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>Refresh</Button>
          {isSuperadmin ? <Button onClick={collectUsage}>Collect Usage</Button> : null}
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Tenant Cloud</p>
          <h3 className="mt-2 text-2xl font-semibold tracking-tight">{currentSection.label}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{currentSection.description}</p>
        </div>

        {currentSectionKey === 'hierarchy' ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            {navigationCards.map((section) => {
              const isActive = section.key === currentSectionKey;
              const Icon = section.icon;
              const [metricValue, ...metricRest] = String(section.metric || '').split(' ');
              const metricLabel = metricRest.join(' ');
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => onNavigate?.(section.id)}
                  className={`card-interactive group rounded-2xl border p-4 text-left ${
                    isActive
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                      : 'border-border bg-card text-card-foreground hover:border-primary/40 hover:bg-primary/[0.03]'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    {Icon ? (
                      <span
                        className={`grid h-8 w-8 place-items-center rounded-lg transition-transform group-hover:scale-105 ${
                          isActive ? 'bg-primary-foreground/15 text-primary-foreground' : 'bg-primary/10 text-primary'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                    ) : null}
                    <p
                      className={`text-xs font-medium uppercase tracking-[0.18em] ${
                        isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'
                      }`}
                    >
                      {section.label}
                    </p>
                  </div>
                  <p className="mt-3 flex items-baseline gap-1.5">
                    <span
                      className={`text-2xl font-bold leading-none tracking-tight tnum ${
                        isActive ? 'text-primary-foreground' : 'text-foreground'
                      }`}
                    >
                      {metricValue}
                    </span>
                    <span className={`text-xs ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                      {metricLabel}
                    </span>
                  </p>
                  <p className={`mt-3 text-sm ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                    {section.description}
                  </p>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {renderActiveSection()}
    </div>
  );
};

export default SuperadminControlPlanePage;
